import { useDeleteOrderQuoteItemAttachmentMutation } from '@codezo/graphql';
import { FileGrid, FileTile, fileMeta } from '@codezo/quote-detail';
import { Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getMutationError } from '../../lib/mutation-response';
import { cn } from '../../lib/utils';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { FilePreview, type FilePreviewItem } from '../ui/file-preview';
import { QuoteItemFileModal, type QuoteItemFileModalMode } from './modals/QuoteItemFileModal';

export interface QuoteItemFile {
	id: string;
	name: string;
	/** What the file is and why it is there. Client-visible. Null on files that
	 * pre-date descriptions, and on any uploaded without one. */
	description?: string | null;
	mimeType: string;
	size: number;
	url?: string | null;
}

/**
 * The files on one product in a quote — tech packs, artwork, spec sheets.
 *
 * They describe the product, not the terms quoted for it, so they hang off the
 * quote line and every offer made for that line serves this same list. Upload
 * once and it shows on every offer, in every batch, including batches created
 * later. That is also why deleting is worth a warning: the file leaves every
 * offer at once, sent ones included.
 *
 * This is the only place files are added, described or removed. Offer cards list
 * them and nothing more.
 *
 * Upload opens a dialog rather than the OS picker: the files are chosen inside
 * it, each with its description beside it, because a description is worth having
 * and the moment to write it is while the admin still knows why they picked the
 * file. It is also where the reach of an upload is said — "shown on every offer
 * for this product" — which is the moment that reach is a decision, rather than a
 * line under every product on the page. See `QuoteItemFileModal`.
 */
export function QuoteItemFiles({
	itemId,
	files,
	canEdit,
	onChanged,
	uploadOpen,
	onUploadOpenChange,
	className,
}: {
	itemId: string;
	files: QuoteItemFile[];
	/** False on a closed quote, where the line is a record rather than a draft. */
	canEdit: boolean;
	/** Refetch the quote — the list is server state, not local. */
	onChanged: () => void;
	/**
	 * Open the upload dialog. "Add files" is an item in the product's overflow
	 * menu, which the page owns and this component is not inside — so the page
	 * asks, and this component still owns the dialog and every mutation in it.
	 */
	uploadOpen?: boolean;
	onUploadOpenChange?: (open: boolean) => void;
	className?: string;
}) {
	const [previewIndex, setPreviewIndex] = useState<number | null>(null);
	const [removeTarget, setRemoveTarget] = useState<QuoteItemFile | null>(null);
	const [removing, setRemoving] = useState(false);
	// Held while the dialog animates out, so its body doesn't blank mid-close.
	const [fileModal, setFileModal] = useState<QuoteItemFileModalMode | null>(null);
	const [fileModalOpen, setFileModalOpen] = useState(false);

	const [deleteFile] = useDeleteOrderQuoteItemAttachmentMutation();

	/* Set directly rather than through `openModal`, so the effect depends on the
	   flag alone and cannot re-fire on an unrelated render. */
	useEffect(() => {
		if (!uploadOpen) return;
		setFileModal({ kind: 'upload' });
		setFileModalOpen(true);
	}, [uploadOpen]);

	// Only files whose URL has resolved can be opened; the rows still list the
	// rest, so a just-uploaded file doesn't vanish while the URL catches up.
	const previewFiles: FilePreviewItem[] = files
		.filter((f) => f.url)
		.map((f) => ({
			id: f.id,
			url: f.url as string,
			name: f.name,
			mimeType: f.mimeType,
			size: f.size,
		}));

	const openModal = (mode: QuoteItemFileModalMode) => {
		setFileModal(mode);
		setFileModalOpen(true);
	};

	const handleRemove = async () => {
		if (!removeTarget) return;
		setRemoving(true);
		try {
			const { data } = await deleteFile({
				variables: { orderQuoteItemId: itemId, attachmentId: removeTarget.id },
			});
			const error = getMutationError(data?.deleteOrderQuoteItemAttachment);
			if (error) throw new Error(error);
			toast.success('File deleted');
			setRemoveTarget(null);
			onChanged();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete file');
		} finally {
			setRemoving(false);
		}
	};

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			{/* No heading over an empty grid: "Files 0" and "no files" say the same
			    thing twice, and the empty state is the whole section.

			    Empty is still a state, not an absence — and there is no Add button
			    here to hold the place. Uploading is an item in the product's menu:
			    one control per product beats a second one that appears in the corner
			    of the card only when the quote is open. */}
			{files.length === 0 ? (
				<span className="text-sm text-text-4">
					No files on this product{canEdit ? ' — add them from the product’s menu.' : '.'}
				</span>
			) : (
				<span className="text-xs font-medium tracking-[0.14em] text-text-3 uppercase">
					Files
					<span className="ml-2 font-mono text-xs font-normal tracking-normal text-text-4 normal-case">
						{files.length}
					</span>
				</span>
			)}

			{files.length > 0 && (
				<FileGrid>
					{files.map((file) => (
						<FileTile
							key={file.id}
							name={file.name}
							meta={fileMeta(file.name, file.mimeType, file.size)}
							description={file.description}
							/* Only a real image goes in an <img>: handing one the raw url
							   of a PDF renders a broken-image glyph. Everything else gets
							   the tile's own file mark. */
							preview={
								file.mimeType.startsWith('image/') && file.url ? (
									<img
										src={file.url}
										alt=""
										className="h-full w-full object-cover"
									/>
								) : undefined
							}
							onOpen={
								file.url
									? () => {
											const idx = previewFiles.findIndex(
												(f) => f.id === file.id
											);
											if (idx >= 0) setPreviewIndex(idx);
										}
									: undefined
							}
							actions={
								canEdit ? (
									<>
										<button
											type="button"
											title={
												file.description
													? 'Edit this description'
													: 'Describe this file'
											}
											onClick={() => openModal({ kind: 'edit', file })}
											className="cursor-pointer rounded-[var(--r-1)] p-1.5 text-text-4 transition-colors hover:text-text-1"
										>
											<Pencil size={12} />
										</button>
										<button
											type="button"
											title="Delete this file"
											onClick={() => setRemoveTarget(file)}
											className="cursor-pointer rounded-[var(--r-1)] p-1.5 text-text-4 transition-colors hover:text-status-danger"
										>
											<Trash2 size={12} />
										</button>
									</>
								) : undefined
							}
						/>
					))}
				</FileGrid>
			)}

			{previewIndex !== null && (
				<FilePreview
					files={previewFiles}
					initialIndex={previewIndex}
					onClose={() => setPreviewIndex(null)}
				/>
			)}

			<QuoteItemFileModal
				open={fileModalOpen}
				onOpenChange={(open) => {
					setFileModalOpen(open);
					if (!open) onUploadOpenChange?.(false);
				}}
				itemId={itemId}
				mode={fileModal}
				onSuccess={onChanged}
			/>

			<ConfirmDialog
				open={!!removeTarget}
				onOpenChange={(v) => !v && setRemoveTarget(null)}
				title="Delete file"
				/* The reach is the whole point of the warning. A file used to belong
				   to one offer; now it belongs to the product, so deleting it pulls
				   it out of offers the client has already been sent. */
				description={`"${removeTarget?.name}" will be removed from every offer for this product, including any already sent to the client.`}
				confirmLabel="Delete"
				variant="destructive"
				loading={removing}
				onConfirm={handleRemove}
			/>
		</div>
	);
}
